import { addDays, subMilliseconds } from 'date-fns';

import {
  ClientAreaApiClient,
  CustomerTransactionStatus,
  CustomerTransactionType,
  DepositMethod,
  DepositSource,
  TradingType,
  WalletType,
} from 'common/api/autogenerated/client-area';

const clientAreaApiClient = new ClientAreaApiClient({
  BASE: '/client-area',
  WITH_CREDENTIALS: true,
});

const TRANSACTIONS_START_PAGE = 1;
export const TRANSACTIONS_COUNT = 400;

const _returnFormattedDates = (dateFrom?: Date | undefined, dateTo?: Date | undefined) => {
  const dateFromStringified = !dateFrom ? undefined : new Date(dateFrom).toISOString();
  const dateToStringified = !dateTo
    ? undefined
    : new Date(subMilliseconds(addDays(new Date(dateTo), 1), 1)).toISOString();

  return {
    dateFrom: dateFromStringified,
    dateTo: dateToStringified,
  };
};

export const getWallets = () => clientAreaApiClient.walletV2.getApiV2WalletAccounts();

export const getTransactions = (
  currencyType?: WalletType,
  currency?: string,
  dateFrom?: Date | undefined,
  dateTo?: Date | undefined,
  accountTradingType?: TradingType,
  types?: Array<CustomerTransactionType>,
  status?: CustomerTransactionStatus,
) => {
  const { dateFrom: _dateFrom, dateTo: _dateTo } = _returnFormattedDates(dateFrom, dateTo);
  return clientAreaApiClient.walletV2.getApiV2WalletTransactions(
    TRANSACTIONS_COUNT,
    _dateFrom,
    _dateTo,
    currency,
    currencyType,
    accountTradingType,
    types,
    status,
  );
};

export const getBalance = () => clientAreaApiClient.wallet.getApiWalletDailyChange();

export const getCurrencies = (currency?: string) => clientAreaApiClient.currencies.getApiCurrencies(currency);

export const getPaymentTypes = (currency?: string) =>
  clientAreaApiClient.deposit.getApiDepositsPaymentMethods(currency);

export const getDepositUrl = (depositMethod = DepositMethod.CREDIT_CARDS) =>
  clientAreaApiClient.deposit.getApiDepositsUrl(depositMethod, DepositSource.WEB);

export const getWalletConfiguration = () => clientAreaApiClient.wallet.getApiWalletConfiguration();

export const getCryptoTransactionDetails = (id: number) =>
  clientAreaApiClient.wallet.getApiWalletCryptoTransactions(id);

export const getFiatTransactionDetails = (id: number) => clientAreaApiClient.wallet.getApiWalletFiatTransactions(id);

export const getTransferTransactionDetails = (id: number) =>
  clientAreaApiClient.wallet.getApiWalletAccountsTransfers(id);

export const getTransactionDetails = (
  id: string,
  type: CustomerTransactionType,
  currencyType?: WalletType | undefined,
) => clientAreaApiClient.walletV2.getApiV2WalletTransactionsDetails(id, type, currencyType);

export const getTransactionsDetailsForCSV = (
  currencyType?: WalletType,
  currency?: string,
  dateFrom?: Date | undefined,
  dateTo?: Date | undefined,
  page: number = TRANSACTIONS_START_PAGE,
) => {
  const { dateFrom: _dateFrom, dateTo: _dateTo } = _returnFormattedDates(dateFrom, dateTo);
  return clientAreaApiClient.wallet.getApiWalletTransactionsDetails(
    currencyType,
    currency,
    page,
    1000,
    _dateFrom,
    _dateTo,
  );
};

export const updateNote = (
  id: string | number,
  type: CustomerTransactionType,
  currencyType: WalletType,
  comment: string,
) =>
  clientAreaApiClient.walletV2.putApiV2WalletTransactionsComment(String(id), type, currencyType, {
    comment: comment || null,
  });

export const getInOrdersForSpot = (currency?: string) =>
  clientAreaApiClient.wallet.getApiWalletPendingWithdrawals(currency);

export const getPaymentCard = () =>
  clientAreaApiClient.paymentCard.getApiPaymentCards().then((response) => {
    const id = Number(response[0].id);
    if (id) return clientAreaApiClient.paymentCard.getApiPaymentCards1(id);

    return undefined;
  });
